<template>
    <div class="login-container">
        <div class="login-wrapper">      
            <div class="left-section">
                <div class="brand-content">
                    <div class="logo-container">
                        <img  :src="$api.ste.logo" class="logo-image"/>            
                    </div>          
                    <div class="illustration">
                        <i class="fas fa-users-cog illustration-icon"></i>
                        <h3 class="illustration-title">Gestion simplifiée</h3>
                        <p class="illustration-text">
                            Votre plateforme professionnelle pour la gestion complète de vos contrats d'assurance et de vos clients.
                        </p>
                    </div>          
                    <div class="security-badges">
                        <div class="badge-item">
                            <i class="fas fa-lock"></i>
                            <span>Sécurisé SSL</span>
                        </div>
                        <div class="badge-item">
                            <i class="fas fa-shield-alt"></i>
                            <span>Données protégées</span>
                        </div>
                    </div>
                </div>
            </div>      
            <div class="right-section">
                <div class="form-container">                              
                    <div v-if="currentStep === 1" class="auth-form step-1">
                        <div class="form-header">
                            <h1 class="form-title">Connexion à votre espace assureur</h1>
                            <p class="form-subtitle">Accédez à votre tableau de bord professionnel</p>
                        </div>
                        <b-form @submit.prevent="handleLogin" class="login-form">
                            <b-form-group label="Adresse email" label-for="email">
                                <b-form-input id="email" v-model="loginForm.email" type="email" placeholder="votre@email.com" :state="emailValidation" required size="lg" class="custom-input"/>
                                <b-form-invalid-feedback>
                                    Veuillez saisir une adresse email valide
                                </b-form-invalid-feedback>
                            </b-form-group>
                            <b-form-group label="Mot de passe" label-for="password">
                                <b-form-input id="password" v-model="loginForm.password" :type="showPassword ? 'text' : 'password'" placeholder="Votre mot de passe" :state="passwordValidation" required size="lg" class="custom-input"/>
                                <b-button variant="link" class="password-toggle mt-3" @click="showPassword = !showPassword" > <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i></b-button>
                                <b-form-invalid-feedback>
                                    Le mot de passe doit contenir au moins 8 caractères
                                </b-form-invalid-feedback>
                            </b-form-group>
                            <b-form-checkbox v-model="loginForm.rememberMe" class="remember-me-checkbox">
                                Se souvenir de moi
                            </b-form-checkbox>
                            <b-alert v-if="loginError" variant="danger" show class="error-alert">
                                <i class="fas fa-exclamation-triangle"></i>
                                {{ loginError }}
                            </b-alert>
                            <b-button type="submit" variant="primary" size="lg" class="login-button" :disabled="isLoading || !isLoginFormValid" block>
                                <b-spinner v-if="isLoading" small class="mr-2"></b-spinner>
                                <i v-else class="fas fa-sign-in-alt mr-2"></i>
                                {{ isLoading ? 'Connexion...' : 'Se connecter' }}
                            </b-button>
              
                            <div class="forgot-password-link">
                                <b-link href="mailto:support@jg-connect.com">
                                    <i class="fas fa-key mr-1"></i>
                                    Mot de passe oublié ?
                                </b-link>
                            </div>
                        </b-form>
                    </div>          
                    <div v-if="currentStep === 2" class="auth-form step-2">
                        <div class="form-header">
                            <h1 class="form-title">Authentification à double facteur</h1>
                            <p class="form-subtitle">
                                Un code de vérification a été envoyé à<br>
                                <strong>{{ maskedEmail }}</strong>
                            </p>
                        </div>
                        <b-form @submit.prevent="handleVerifyCode" class="verification-form">
                            <b-form-group label="Code de vérification" label-for="verificationCode">
                                <b-form-input id="verificationCode" v-model="verificationCode" type="text" placeholder="000000" maxlength="6" size="lg" class="verification-input" :state="codeValidation" @input="handleCodeInput"/>
                                <b-form-invalid-feedback>
                                    Le code doit contenir exactement 6 chiffres
                                </b-form-invalid-feedback>
                            </b-form-group>
                            <div class="timer-container">
                                <div class="timer-display">
                                    <i class="fas fa-clock mr-2"></i>
                                    Code valide pendant : {{ formattedTimeLeft }}
                                </div>
                                <b-progress :value="timerProgress" variant="warning" height="4px" class="timer-progress"></b-progress>
                            </div>
                            <b-alert v-if="verificationError" variant="danger" show class="error-alert">
                                <i class="fas fa-exclamation-triangle"></i>
                                {{ verificationError }}
                            </b-alert>
                            <div class="verification-buttons">
                                <b-button type="submit" variant="primary" size="lg" class="verify-button" :disabled="isVerifying || !isCodeValid" block>
                                    <b-spinner v-if="isVerifying" small class="mr-2"></b-spinner>
                                    <i v-else class="fas fa-check mr-2"></i>
                                    {{ isVerifying ? 'Vérification...' : 'Valider le code' }}
                                </b-button>                
                                <b-button variant="outline-primary" size="lg" class="resend-button" :disabled="canResendCode || isResending" @click="handleResendCode" block>
                                    <b-spinner v-if="isResending" small class="mr-2"></b-spinner>
                                    <i v-else class="fas fa-paper-plane mr-2"></i>
                                    {{ isResending ? 'Envoi...' : 'Renvoyer le code' }}
                                </b-button>
                            </div>
                            <div class="back-link">
                                <b-link @click="goBackToLogin">
                                    <i class="fas fa-arrow-left mr-1"></i>
                                    Retour à la connexion
                                </b-link>
                            </div>
                        </b-form>
                    </div>                    
                </div>                
                <div class="login-footer">
                    <div class="footer-links">            
                        <b-link href="mailto:support@jg-connect.com">Support</b-link>
                    </div>
                    <div class="copyright">
                        © {{ $api.moment().format("YYYY") }} JG Connect. Tous droits réservés.
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
  name: 'LoginPage',
  
  data() {
    return {
      // État des formulaires
      currentStep: 1,
      isLoading: false,
      isVerifying: false,
      isResending: false,
      showPassword: false,

      // Données du formulaire de connexion
      loginForm: {
        email: null,
        password: null,
        rememberMe: false
      },

      // Données de vérification
      verificationCode: '',
      timeLeft: 300, // 5 minutes en secondes
      timerInterval: null,

      // Messages d'erreur
      loginError: '',
      verificationError: ''
    }
  },

  computed: {
    emailValidation() {
      if (!this.loginForm.email) return null
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(this.loginForm.email) ? true : false
    },

    passwordValidation() {
      if (!this.loginForm.password) return null
      return this.loginForm.password.length >= 8 ? true : false
    },

    isLoginFormValid() {
      return this.emailValidation === true && this.passwordValidation === true
    },

    codeValidation() {
      if (!this.verificationCode) return null
      return this.verificationCode.length === 6 && /^\d+$/.test(this.verificationCode) ? true : false
    },

    isCodeValid() {
      return this.codeValidation === true
    },

    maskedEmail() {
      const email = this.loginForm.email
      const [localPart, domain] = email.split('@')
      const maskedLocal = localPart.charAt(0) + '*'.repeat(localPart.length - 2) + localPart.charAt(localPart.length - 1)
      return `${maskedLocal}@${domain}`
    },

    formattedTimeLeft() {
      const minutes = Math.floor(this.timeLeft / 60)
      const seconds = this.timeLeft % 60
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    },

    timerProgress() {
      return (this.timeLeft / 300) * 100
    },

    canResendCode() {
      return this.timeLeft > 240 // Peut renvoyer seulement après 1 minute
    }
  },

  methods: {
    async handleLogin() {
        if (!this.isLoginFormValid || this.isLoading) return      
        this.isLoading = true
        this.loginError = ''
        
        this.$api.ajax('/authorization/create', this.loginForm, res => {
            if(res.status === true){
                if(res.data.double_auth === true){
                    this.currentStep = 2
                    this.startTimer()
                }else{
                    this.setToken(res.data);
                }            
            }else{
                this.loginError = res.data;
            }
            this.isLoading = false
        });      
    },
    setToken(data){
        if(this.loginForm.rememberMe === true){
            var user = JSON.stringify(this.loginForm);
            localStorage.credentials = user
        }
        this.$api.saveToken(data.token);
        this.$api.saveUser(data);
        this.$router.push({path:"/agenda/mois"});
    },
    async handleVerifyCode() {
      if (!this.isCodeValid || this.isVerifying) return      
      this.isVerifying = true
      this.verificationError = ''
      let params = {...this.loginForm, code: this.verificationCode};
      this.$api.ajax('/authorization/2fa', params, res => {
        if(res.status === true){
            this.setToken(res.data);
        }else{
            this.verificationError = res.data
        }
        this.isVerifying = false;
      })      
    },

    async handleResendCode() {
        this.isResending = true
        this.$api.ajax('/authorization/create', this.loginForm, () => {
            this.isResending = false
        });           
    },

    handleCodeInput(value) {
      // Ne permettre que les chiffres
      this.verificationCode = value.replace(/\D/g, '')
    },    
    goBackToLogin() {
      this.currentStep = 1
      this.verificationCode = ''
      this.verificationError = ''
      this.stopTimer()
    },
    startTimer() {
      this.timerInterval = setInterval(() => {
        if (this.timeLeft > 0) {
          this.timeLeft--
        } else {
          this.stopTimer()
          this.verificationError = 'Le code de vérification a expiré. Veuillez en demander un nouveau.'
        }
      }, 1000)
    },
    stopTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
        this.timerInterval = null
      }
    }
  },

  mounted() {
    // Les données de test sont déjà définies dans data()
  },
  beforeMount() {    
    if(localStorage.credentials != undefined){
      var user = JSON.parse(localStorage.credentials);
      this.loginForm = user;
    }
    if(localStorage.token){
      this.$router.push({path:"/agenda/mois"});
    }
  },
  beforeUnmount() {
    this.stopTimer()
  }
}
</script>
<style>
</style>
<style scoped>
.login-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.login-wrapper {
  width: 100%;
  max-width: 1200px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  display: flex;
  min-height: 600px;
}

/* Section gauche */
.left-section {
  flex: 0 0 40%;
  background: linear-gradient(135deg, #406cae 0%, #2c5282 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  position: relative;
}

.left-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
  opacity: 0.3;
}

.brand-content {
  position: relative;
  z-index: 1;
  text-align: center;
}

.logo-container {
  margin-bottom: 40px;
}

.logo-icon {
  font-size: 4rem;
  margin-bottom: 16px;
  display: block;
  color: #ffffff;
}

.brand-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  color: #ffffff;
}

.illustration {
  margin: 40px 0;
}

.illustration-icon {
  font-size: 6rem;
  margin-bottom: 24px;
  display: block;
  color: rgba(255, 255, 255, 0.9);
}

.illustration-title {
  font-size: 1.8rem;
  font-weight: 600;
  margin-bottom: 16px;
  color: #ffffff;
}

.illustration-text {
  font-size: 1.1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  max-width: 300px;
  margin: 0 auto;
}

.security-badges {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 40px;
}

.badge-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
}

.badge-item i {
  font-size: 1.1rem;
}

/* Section droite */
.right-section {
  flex: 0 0 60%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 60px 80px 40px;
  position: relative;
}

.form-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.auth-form {
  width: 100%;
  max-width: 400px;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.form-header {
  text-align: center;
  margin-bottom: 40px;
}

.form-title {
  font-size: 2rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
  line-height: 1.2;
}

.form-subtitle {
  font-size: 1.1rem;
  color: #718096;
  margin: 0;
  line-height: 1.5;
}

/* Styles des formulaires */
.login-form, .verification-form {
  margin-bottom: 32px;
}

.custom-input, .verification-input {
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1.1rem;
  padding: 16px 18px;
  transition: all 0.3s ease;
  background: #ffffff;
}

.custom-input:focus, .verification-input:focus {
  border-color: #406cae;
  box-shadow: 0 0 0 3px rgba(64, 108, 174, 0.1);
  background: #ffffff;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  padding: 8px;
  color: #a0aec0;
  text-decoration: none;
}

.password-toggle:hover {
  color: #406cae;
}

.form-group {
  position: relative;
  margin-bottom: 24px;
}

.form-group label {
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 8px;
  font-size: 0.95rem;
}

.remember-me-checkbox {
  margin: 24px 0;
}

.remember-me-checkbox >>> .custom-control-label {
  color: #4a5568;
  font-size: 0.95rem;
}

.error-alert {
  border-radius: 12px;
  border: none;
  padding: 16px;
  margin: 24px 0;
  background: #fed7d7;
  color: #c53030;
  border-left: 4px solid #e53e3e;
}

.login-button, .verify-button {
  background: linear-gradient(135deg, #dd4835 0%, #c53030 100%);
  border: none;
  border-radius: 12px;
  padding: 16px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  margin: 24px 0;
}

.login-button:hover, .verify-button:hover {
  background: linear-gradient(135deg, #c53030 0%, #a02622 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(221, 72, 53, 0.3);
}

.login-button:disabled, .verify-button:disabled {
  background: #cbd5e0;
  transform: none;
  box-shadow: none;
}

.resend-button {
  border: 2px solid #406cae;
  color: #406cae;
  border-radius: 12px;
  padding: 16px;
  font-weight: 600;
  font-size: 1.1rem;
  background: transparent;
  transition: all 0.3s ease;
  margin-top: 16px;
}

.resend-button:hover {
  background: #406cae;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(64, 108, 174, 0.3);
}

.resend-button:disabled {
  border-color: #cbd5e0;
  color: #cbd5e0;
  background: transparent;
  transform: none;
  box-shadow: none;
}

.forgot-password-link, .back-link {
  text-align: center;
  margin-top: 24px;
}

.forgot-password-link a, .back-link a {
  color: #406cae;
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  transition: color 0.3s ease;
}

.forgot-password-link a:hover, .back-link a:hover {
  color: #2c5282;
  text-decoration: none;
}

/* Styles spécifiques à l'étape 2 */
.verification-input {
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: 8px;
  font-family: 'Courier New', monospace;
}

.timer-container {
  margin: 24px 0;
  text-align: center;
}

.timer-display {
  font-size: 0.95rem;
  color: #f6ad55;
  font-weight: 600;
  margin-bottom: 8px;
}

.timer-progress {
  border-radius: 8px;
  overflow: hidden;
}

.verification-buttons {
  margin: 32px 0;
}

/* Footer */
.login-footer {
  margin-top: auto;
  padding-top: 32px;
  border-top: 1px solid #e2e8f0;
  text-align: center;
}

.footer-links {
  margin-bottom: 12px;
}

.footer-links a {
  color: #718096;
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s ease;
}

.footer-links a:hover {
  color: #406cae;
  text-decoration: none;
}

.separator {
  margin: 0 12px;
  color: #cbd5e0;
}

.copyright {
  font-size: 0.85rem;
  color: #a0aec0;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .login-container {
    min-height: initial !important;    
  }
  .logo-container {
    margin-bottom: 0px !important;
  }
  .illustration, .security-badges {
    display: none;
  }
  .login-wrapper {
    flex-direction: column;
    max-width: 500px;
  }
  
  .left-section {
    flex: 0 0 auto;
    padding: 40px 32px;
  }
  
  .right-section {
    padding: 40px 32px 32px;
  }
  
  .illustration-icon {
    font-size: 4rem;
  }
  
  .form-title {
    font-size: 1.75rem;
  }
}

@media (max-width: 768px) {
  .login-container {
    padding: 16px;
  }
  
  .login-wrapper {
    border-radius: 16px;
    max-width: 400px;
  }
  
  .left-section {
    padding: 32px 24px;
  }
  
  .right-section {
    padding: 32px 24px 24px;
  }
  
  .brand-title {
    font-size: 2rem;
  }
  
  .form-title {
    font-size: 1.5rem;
  }
  
  .illustration {
    margin: 24px 0;
  }
  
  .security-badges {
    flex-direction: column;
    gap: 12px;
  }
}

@media (max-width: 480px) {
  .left-section {
    padding: 24px 16px;
  }
  
  .right-section {
    padding: 24px 16px 16px;
  }
  
  .brand-title {
    font-size: 1.75rem;
  }
  
  .logo-icon {
    font-size: 3rem;
  }
  
  .illustration-icon {
    font-size: 3rem;
  }
  
  .illustration-title {
    font-size: 1.4rem;
  }
  
  .form-title {
    font-size: 1.3rem;
  }
}
</style>